<template>
  <aside class="personal-sidebar">
    <div class="personal-sidebar__tabs">
      <button
        class="personal-sidebar__tab"
        :class="{ 'personal-sidebar__tab--active': activeTab === 'for-you' }"
        @click="activeTab = 'for-you'"
      >
        For You
      </button>
      <button
        v-if="currentUser"
        class="personal-sidebar__tab"
        :class="{ 'personal-sidebar__tab--active': activeTab === 'topics' }"
        @click="showTopics"
      >
        Topics
      </button>
    </div>

    <ContentBasedSuggestionsSideBar v-if="activeTab === 'for-you'" />

    <div v-else class="topics-side">
      <div class="topics-side__header">
        <p class="topics-side__eyebrow">Your Topics</p>
        <h3>Stories matched to what you follow</h3>
      </div>

      <div v-if="topicsLoading" class="topics-side__state">
        <i class="fa-solid fa-spinner fa-spin"></i>
        Loading your topic feed...
      </div>

      <div v-else-if="topicsArticles.length === 0" class="topics-side__state">
        <i class="fa fa-rss" aria-hidden="true"></i>
        Follow topics from the Sentiment Pulse to build this feed.
      </div>

      <template v-else>
        <div class="topics-side__filters">
          <button
            class="topics-side__filter"
            :class="{ 'topics-side__filter--active': activeTopicFilter === null }"
            @click="activeTopicFilter = null"
          >
            All <span>{{ topicsArticles.length }}</span>
          </button>
          <button
            v-for="topic in topicFilters"
            :key="topic.name"
            class="topics-side__filter"
            :class="{ 'topics-side__filter--active': activeTopicFilter === topic.name }"
            @click="activeTopicFilter = topic.name"
          >
            {{ topic.name }} <span>{{ topic.count }}</span>
          </button>
        </div>

        <div v-if="filteredTopicsArticles.length === 0" class="topics-side__state">
          No articles matched this topic anymore.
        </div>

        <div v-else class="topics-side__list">
          <article
            v-for="article in filteredTopicsArticles.slice(0, 12)"
            :key="article.id"
            class="topics-side__article"
          >
            <img
              :src="gridArticleThumb(article.imageUrl)"
              loading="lazy"
              :alt="article.title || 'Article'"
            />
            <a
              :href="article.urlPath || articleHref(article)"
              class="topics-side__title"
              @click.prevent="pushToArticle(article.urlPath || articleHref(article))"
            >
              {{ article.title }}
            </a>
            <div class="topics-side__matched">
              <span
                v-for="topic in article.matchedTopics"
                :key="topic"
                class="topics-side__matched-chip"
              >
                {{ topic }}
                <button
                  type="button"
                  :aria-label="`Unfollow ${topic}`"
                  title="Unfollow topic"
                  @click.stop.prevent="unfollowTopic(topic)"
                >
                  <i class="fa fa-times" aria-hidden="true"></i>
                </button>
              </span>
            </div>
          </article>
        </div>
      </template>
    </div>
  </aside>
</template>

<script>
import { mapState } from 'vuex';
import ArticleDataService from '@/services/ArticleDataService';
import ContentBasedSuggestionsSideBar from '@/components/ContentBasedSuggestionsSideBar.vue';
import { optimizeImage } from '@/utils/imageOptimize';

export default {
  name: 'PersonalizedSidebar',
  components: { ContentBasedSuggestionsSideBar },
  data() {
    return {
      activeTab: 'for-you',
      topicsArticles: [],
      topicsLoading: false,
      activeTopicFilter: null,
    };
  },
  computed: {
    ...mapState({
      currentUser: state => state.user.id,
    }),
    topicFilters() {
      const counts = new Map();
      this.topicsArticles.forEach(article => {
        (article.matchedTopics || []).forEach(topic => {
          counts.set(topic, (counts.get(topic) || 0) + 1);
        });
      });
      return Array.from(counts, ([name, count]) => ({ name, count }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name));
    },
    filteredTopicsArticles() {
      if (!this.activeTopicFilter) return this.topicsArticles;
      return this.topicsArticles.filter(article =>
        (article.matchedTopics || []).includes(this.activeTopicFilter)
      );
    },
  },
  methods: {
    gridArticleThumb(url) {
      return optimizeImage(url, 180, 102);
    },
    articleHref(article) {
      const slug = (article.title || '')
        .toLowerCase()
        .replace(/\W+/g, '-')
        .replace(/^-|-$/g, '');
      return `/${article.categorySpecified}/${slug}/${article.id}`;
    },
    showTopics() {
      this.activeTab = 'topics';
      if (this.topicsArticles.length === 0 && !this.topicsLoading) {
        this.fetchTopicsFeed();
      }
    },
    async fetchTopicsFeed() {
      if (!this.currentUser) return;
      this.topicsLoading = true;
      try {
        const res = await ArticleDataService.getMyTopicsFeed();
        this.topicsArticles = Array.isArray(res.data) ? res.data : [];
      } catch {
        this.topicsArticles = [];
      } finally {
        this.topicsLoading = false;
      }
    },
    async unfollowTopic(tag) {
      try {
        await ArticleDataService.unfollowTopic(tag);
      } catch (err) {
        if (err?.response?.status !== 404) return;
      }
      this.topicsArticles = this.topicsArticles
        .map(article => ({
          ...article,
          matchedTopics: (article.matchedTopics || []).filter(topic => topic !== tag),
        }))
        .filter(article => article.matchedTopics.length > 0);
      if (this.activeTopicFilter === tag) this.activeTopicFilter = null;
    },
    pushToArticle(pathOrCategory, title, id) {
      if (typeof pathOrCategory === 'string' && pathOrCategory.startsWith('/')) {
        if (this.$route.path !== pathOrCategory) {
          this.$router.push({ path: pathOrCategory });
        }
        return;
      }
      const category = pathOrCategory;
      const slug = (title || '')
        .toLowerCase()
        .replace(/\W+/g, '-')
        .replace(/^-|-$/g, '');
      const fallbackPath = `/${category || 'business'}/${slug}/${id}`;
      if (this.$route.path !== fallbackPath) {
        this.$router.push({ path: fallbackPath });
      }
    },
  },
};
</script>

<style scoped>
.personal-sidebar {
  width: 20rem;
  background: #f4f1f1;
}

.personal-sidebar__tabs {
  display: flex;
  gap: 6px;
  padding: 8px;
  background: #fff;
  border: 1px solid #aaa8a8;
  border-bottom: none;
}

.personal-sidebar__tab {
  flex: 1;
  min-height: 34px;
  border: 1.5px solid #d1d5db;
  border-radius: 999px;
  background: #fff;
  color: #374151;
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;
}

.personal-sidebar__tab--active {
  background: #0b7ee3;
  border-color: #0b7ee3;
  color: #fff;
}

.topics-side {
  border: 1px solid #aaa8a8;
  background: #f8fafc;
}

.topics-side__header {
  padding: 12px;
  background: linear-gradient(135deg, #f8fbff 0%, #eef6ff 100%);
  border-bottom: 1px solid #dbeafe;
}

.topics-side__eyebrow {
  margin: 0 0 3px;
  color: #0b7ee3;
  font-size: 0.68rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.topics-side__header h3 {
  margin: 0;
  color: #111827;
  font-family: 'Titillium Web', sans-serif;
  font-size: 0.95rem;
  line-height: 1.2;
}

.topics-side__filters {
  display: flex;
  gap: 6px;
  padding: 8px;
  overflow-x: auto;
  scrollbar-width: none;
  background: #fff;
  border-bottom: 1px solid #e2e8f0;
}

.topics-side__filters::-webkit-scrollbar {
  display: none;
}

.topics-side__filter {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex-shrink: 0;
  padding: 5px 9px;
  border-radius: 999px;
  border: 1.5px solid #d1d5db;
  background: #fff;
  color: #374151;
  font-size: 0.72rem;
  font-weight: 800;
  white-space: nowrap;
  cursor: pointer;
}

.topics-side__filter span {
  min-width: 18px;
  padding: 2px 5px;
  border-radius: 999px;
  background: #eef2ff;
  color: #0b65c2;
}

.topics-side__filter--active {
  background: #0b7ee3;
  border-color: #0b7ee3;
  color: #fff;
}

.topics-side__filter--active span {
  background: rgba(255, 255, 255, 0.22);
  color: #fff;
}

.topics-side__state {
  padding: 24px 14px;
  color: #64748b;
  font-size: 0.82rem;
  line-height: 1.45;
  text-align: center;
}

.topics-side__list {
  display: flex;
  flex-direction: column;
}

.topics-side__article {
  padding: 12px;
  border-bottom: 1px solid #e2e8f0;
  background: #fff;
}

.topics-side__article img {
  width: 100%;
  aspect-ratio: 16 / 9;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 8px;
}

.topics-side__title {
  display: block;
  color: #111827;
  font-family: 'Titillium Web', sans-serif;
  font-size: 0.95rem;
  font-weight: 900;
  line-height: 1.25;
  text-decoration: none;
}

.topics-side__title:hover {
  color: #0b7ee3;
}

.topics-side__matched {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  margin-top: 8px;
}

.topics-side__matched-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 7px 3px 8px;
  border-radius: 999px;
  background: #eef6ff;
  color: #0b65c2;
  border: 1px solid rgba(11, 126, 227, 0.18);
  font-size: 0.65rem;
  font-weight: 800;
}

.topics-side__matched-chip button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(11, 126, 227, 0.1);
  color: #0b65c2;
  cursor: pointer;
}

.topics-side__matched-chip button:hover {
  background: #dd3434;
  color: #fff;
}

.topics-side__matched-chip i {
  font-size: 0.55rem;
}
</style>
